import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { ArrowLeft, Building2, Zap, TrendingUp, Activity, MapPin, User, Radio, Clock } from 'lucide-react';
import { sites, districts, complaints, rmsDevices } from '../mock';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const SiteView = () => {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const site = sites.find(s => s.id === siteId);
  const district = site ? districts.find(d => d.id === site.district) : null;
  const siteComplaints = complaints.filter(c => c.site === siteId);
  const rmsDevice = rmsDevices.find(d => d.siteId === siteId);

  if (!site) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/site-directory')}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Site Directory
        </Button>
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-lg" style={{ color: '#576574' }}>Site not found</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getDeviceStatusColor = (status) => {
    if (status === 'online') return '#10ac84';
    if (status === 'warning') return '#f39c12';
    return '#e74c3c';
  };

  const getDeviceStatusBg = (status) => {
    if (status === 'online') return '#e8f5f1';
    if (status === 'warning') return '#fff4e6';
    return '#fee';
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <Button variant="ghost" onClick={() => navigate('/site-directory')} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Site Directory
        </Button>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: '#0a3d62' }}>{site.name}</h1>
            <p className="text-base mt-2 flex items-center gap-2" style={{ color: '#576574' }}>
              <MapPin className="w-4 h-4" />
              {site.address}
            </p>
          </div>
          <Badge
            variant="outline"
            className="px-4 py-2 text-base"
            style={{
              backgroundColor: getDeviceStatusBg(site.status),
              color: getDeviceStatusColor(site.status),
              borderColor: getDeviceStatusColor(site.status)
            }}
          >
            {site.status.toUpperCase()}
          </Badge>
        </div>
      </div>

      {/* Site Info Card */}
      <Card>
        <CardHeader>
          <CardTitle>Site Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <div className="text-sm mb-2" style={{ color: '#576574' }}>Site ID</div>
              <div className="font-mono font-semibold">{site.id}</div>
            </div>
            <div>
              <div className="text-sm mb-2" style={{ color: '#576574' }}>District</div>
              <div className="font-semibold capitalize">{district?.name}</div>
            </div>
            <div>
              <div className="text-sm mb-2" style={{ color: '#576574' }}>Contractor</div>
              <div className="font-semibold">{site.contractor}</div>
            </div>
            <div>
              <div className="text-sm mb-2" style={{ color: '#576574' }}>Last Data Sync</div>
              <div className="font-semibold flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {site.lastSync}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Performance KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#e8f5f1' }}>
                <Zap className="w-6 h-6" style={{ color: '#10ac84' }} />
              </div>
            </div>
            <div className="text-3xl font-bold mb-2" style={{ color: '#0a3d62' }}>{site.capacity} MW</div>
            <div className="text-sm" style={{ color: '#576574' }}>Installed Capacity</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#e3f2fd' }}>
                <TrendingUp className="w-6 h-6" style={{ color: '#3498db' }} />
              </div>
            </div>
            <div className="text-3xl font-bold mb-2" style={{ color: '#0a3d62' }}>{site.todayGen} kWh</div>
            <div className="text-sm" style={{ color: '#576574' }}>Today's Generation</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#fff4e6' }}>
                <Activity className="w-6 h-6" style={{ color: '#f39c12' }} />
              </div>
            </div>
            <div className="text-3xl font-bold mb-2" style={{ color: '#0a3d62' }}>{site.pr}%</div>
            <div className="text-sm" style={{ color: '#576574' }}>Performance Ratio</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#f3e5f5' }}>
                <Activity className="w-6 h-6" style={{ color: '#9b59b6' }} />
              </div>
            </div>
            <div className="text-3xl font-bold mb-2" style={{ color: '#0a3d62' }}>{site.cuf}%</div>
            <div className="text-sm" style={{ color: '#576574' }}>CUF</div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Performance Ratio (PR)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-2">
              <span className="text-2xl font-bold" style={{ color: '#f39c12' }}>{site.pr}%</span>
              <Badge variant="outline" style={{ backgroundColor: site.pr >= 75 ? '#e8f5f1' : site.pr >= 60 ? '#fff4e6' : '#fee', color: site.pr >= 75 ? '#10ac84' : site.pr >= 60 ? '#f39c12' : '#e74c3c' }}>
                {site.pr >= 75 ? 'Excellent' : site.pr >= 60 ? 'Good' : 'Poor'}
              </Badge>
            </div>
            <Progress value={site.pr} className="h-3" />
            <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
              <div>
                <div style={{ color: '#576574' }}>Poor</div>
                <div className="font-semibold">{'<'} 60%</div>
              </div>
              <div>
                <div style={{ color: '#576574' }}>Good</div>
                <div className="font-semibold">60-75%</div>
              </div>
              <div>
                <div style={{ color: '#576574' }}>Excellent</div>
                <div className="font-semibold">{'>'} 75%</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Capacity Utilization Factor (CUF)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-2">
              <span className="text-2xl font-bold" style={{ color: '#9b59b6' }}>{site.cuf}%</span>
              <Badge variant="outline" style={{ backgroundColor: site.cuf >= 19 ? '#e8f5f1' : site.cuf >= 15 ? '#fff4e6' : '#fee', color: site.cuf >= 19 ? '#10ac84' : site.cuf >= 15 ? '#f39c12' : '#e74c3c' }}>
                {site.cuf >= 19 ? 'Excellent' : site.cuf >= 15 ? 'Good' : 'Poor'}
              </Badge>
            </div>
            <Progress value={site.cuf * 5} className="h-3" />
            <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
              <div>
                <div style={{ color: '#576574' }}>Poor</div>
                <div className="font-semibold">{'<'} 15%</div>
              </div>
              <div>
                <div style={{ color: '#576574' }}>Good</div>
                <div className="font-semibold">15-19%</div>
              </div>
              <div>
                <div style={{ color: '#576574' }}>Excellent</div>
                <div className="font-semibold">{'>'} 19%</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Generation Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" style={{ color: '#3498db' }} />
            7-Day Generation Trend
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <Line
              data={{
                labels: ['Day 1', 'Day 2', 'Day 3', 'Day 4', 'Day 5', 'Day 6', 'Day 7'],
                datasets: [{
                  label: 'Generation (kWh)',
                  data: [
                    site.todayGen * 0.8,
                    site.todayGen * 0.9,
                    site.todayGen * 1.1,
                    site.todayGen * 0.95,
                    site.todayGen * 1.05,
                    site.todayGen * 0.85,
                    site.todayGen
                  ],
                  borderColor: '#3498db',
                  backgroundColor: 'rgba(52, 152, 219, 0.1)',
                  tension: 0.4,
                  fill: true,
                }]
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: false,
                  },
                  tooltip: {
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    titleColor: '#fff',
                    bodyColor: '#fff',
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    grid: {
                      color: 'rgba(0, 0, 0, 0.1)',
                    },
                    ticks: {
                      color: '#576574',
                    }
                  },
                  x: {
                    grid: {
                      color: 'rgba(0, 0, 0, 0.1)',
                    },
                    ticks: {
                      color: '#576574',
                    }
                  }
                }
              }}
            />
          </div>
        </CardContent>
      </Card>

      {/* RMS Device Health & Complaints */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* RMS Device Health */}
        {rmsDevice && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Radio className="w-5 h-5" style={{ color: '#3498db' }} />
                RMS Device Health
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 rounded-lg" style={{ backgroundColor: getDeviceStatusBg(rmsDevice.status) }}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm" style={{ color: '#576574' }}>Status</span>
                    <Badge variant="outline" style={{ backgroundColor: 'white', color: getDeviceStatusColor(rmsDevice.status) }}>
                      {rmsDevice.status.toUpperCase()}
                    </Badge>
                  </div>
                  <div className="text-2xl font-bold" style={{ color: getDeviceStatusColor(rmsDevice.status) }}>
                    {rmsDevice.deviceId}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg" style={{ backgroundColor: '#f5f7fa' }}>
                    <div className="text-sm mb-1" style={{ color: '#576574' }}>Last Data</div>
                    <div className="font-semibold">{rmsDevice.lastData}</div>
                  </div>
                  <div className="p-4 rounded-lg" style={{ backgroundColor: '#f5f7fa' }}>
                    <div className="text-sm mb-1" style={{ color: '#576574' }}>Data Points (24h)</div>
                    <div className="font-semibold">{rmsDevice.dataPoints}</div>
                  </div>
                  <div className="p-4 rounded-lg" style={{ backgroundColor: '#f5f7fa' }}>
                    <div className="text-sm mb-1" style={{ color: '#576574' }}>Sync Errors</div>
                    <div className="font-semibold" style={{ color: rmsDevice.syncErrors > 5 ? '#e74c3c' : '#10ac84' }}>
                      {rmsDevice.syncErrors}
                    </div>
                  </div>
                  <div className="p-4 rounded-lg" style={{ backgroundColor: '#f5f7fa' }}>
                    <div className="text-sm mb-1" style={{ color: '#576574' }}>Success Rate</div>
                    <div className="font-semibold">{((rmsDevice.dataPoints / 1440) * 100).toFixed(1)}%</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Complaints/Tickets */}
        <Card>
          <CardHeader>
            <CardTitle>Service Tickets ({siteComplaints.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {siteComplaints.length > 0 ? (
              <div className="space-y-3">
                {siteComplaints.map((complaint) => (
                  <div key={complaint.id} className="p-4 rounded-lg border">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-mono text-sm font-semibold">{complaint.id}</span>
                      <div className="flex gap-2">
                        <Badge variant="outline" style={{ backgroundColor: complaint.priority === 'high' ? '#fee' : complaint.priority === 'medium' ? '#fff4e6' : '#e8f5f1', color: complaint.priority === 'high' ? '#e74c3c' : complaint.priority === 'medium' ? '#f39c12' : '#10ac84' }}>
                          {complaint.priority}
                        </Badge>
                        <Badge variant="outline">{complaint.status}</Badge>
                      </div>
                    </div>
                    <div className="font-medium mb-1">{complaint.category}</div>
                    <div className="text-sm" style={{ color: '#576574' }}>{complaint.description}</div>
                    <div className="text-xs mt-2" style={{ color: '#576574' }}>Created: {complaint.createdAt}</div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: '#576574' }}>No tickets for this site</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SiteView;
